import React from 'react';
import { useIntl } from 'react-intl';

import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { UnitConverter } from 'Util/UnitOfMeasurementUtils';
import { WizardErrors } from 'Views/Components/_HumanWritten/BookingWizard/EventsBookingWizard/EventsBookingWizardData';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { TextInput } from 'Views/Components/New/TextInput';
import { FreightBookingWizardData } from '../../BookingWizardData/FreightBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { roundCubic } from './CalculationHelpers';
import MeasurementInput from './MeasurementInput';

interface ShipmentDimensionsSectionProps {
	typedErrors: WizardErrors<FreightBookingWizardData>;
	onAfterChange: (fieldName: keyof FreightBookingWizardData) => void;
}

export function ShipmentDimensionsSection({ typedErrors, onAfterChange }: ShipmentDimensionsSectionProps) {
	const intl = useIntl();
	const { wizardData } = useBookingWizardDataStore();

	if (wizardData.innerWizardData.type !== 'freight') {
		return <></>;
	}

	const inner = wizardData.innerWizardData;
	const unit = whiteLabelStore.systemMeasurementUnit;

	const onDimensionChange = (dimension: 'length' | 'width' | 'height', value: number) => {
		if (wizardData.innerWizardData.type === 'freight') {
			wizardData.innerWizardData[dimension] = value;
			onAfterChange(dimension);
		}
	};

	const totalWeight = `${((inner.weight ?? 0) * (inner.quantity ?? 0)).toFixed(0)} ${whiteLabelStore.massUnitLabel}`;
	const totalCubic = (() => {
		const value = roundCubic(
			UnitConverter.getCubic({
				length: inner.length ?? 0,
				width: inner.width ?? 0,
				height: inner.height ?? 0,
				unitOfMeasurement: unit,
			}),
		);
		return `${(inner.quantity ?? 0) * value} ${whiteLabelStore.volumeUnitLabel}`;
	})();

	return (
		<div className="freight-dimensions" data-test-id="freight-dimensions-section">
			<header className="booking-section-header">
				<span className="booking-section-heading__icon">
					<Icon name="straighten" useMaterialIcons />
				</span>
				<div className="booking-section-header__text">
					<h4 className="booking-section-title">
						{intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-dimensions-section__label',
							defaultMessage: 'Shipment dimensions',
						})}
					</h4>
				</div>
			</header>
			<div className="freight-dimensions__grid grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
				<NumberInput
					label={intl.formatMessage({
						id: 'freight-booking-wizard__shipment-tab__freight-weight__label',
						defaultMessage: 'Weight (per unit)',
					})}
					placeholder="0"
					required
					allowNegative={false}
					endAdornment={whiteLabelStore.massUnitLabel}
					value={inner.weight ?? undefined}
					onChange={value => {
						inner.weight = value ?? null;
						onAfterChange('weight');
					}}
					error={typedErrors?.weight}
				/>
				<MeasurementInput
					unitType={unit}
					value={inner.length ?? 0}
					onChange={value => onDimensionChange('length', value)}
					preLabel="Length (per unit)"
					required
					error={typedErrors?.length}
				/>
				<MeasurementInput
					unitType={unit}
					value={inner.width ?? 0}
					onChange={value => onDimensionChange('width', value)}
					preLabel="Width (per unit)"
					required
					error={typedErrors?.width}
				/>
				<MeasurementInput
					unitType={unit}
					value={inner.height ?? 0}
					onChange={value => onDimensionChange('height', value)}
					preLabel="Height (per unit)"
					required
					error={typedErrors?.height}
				/>
				<TextInput
					label={intl.formatMessage({
						id: 'freight-booking-wizard__shipment-tab__freight-total-weight__label',
						defaultMessage: 'Total weight',
					})}
					placeholder="0"
					disabled
					value={totalWeight}
				/>
				<TextInput
					label={intl.formatMessage({
						id: 'freight-booking-wizard__shipment-tab__freight-total-volume__label',
						defaultMessage: 'Total cubic',
					})}
					placeholder="0"
					disabled
					value={totalCubic}
				/>
			</div>
		</div>
	);
}
